import { useEffect, useState } from "react";
import { Loader2, ScanFace, ShieldCheck, AlertTriangle } from "lucide-react";
import { api, formatError } from "@/lib/api";

export const IdCheckStep = ({ token, returning, onReleased }) => {
  const [s, setS] = useState(returning ? { status: "processing" } : { status: "not_started" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!returning) { return undefined; }
    let n = 0;
    let stop = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/claim/${token}/id-check/status`);
        if (stop) return;
        setS(data);
        if (data.mail_id) { onReleased(data.mail_id); return; }
        if (["processing", "pending"].includes(data.status) && n++ < 20) setTimeout(poll, 3000);
      } catch (e) {
        setError(formatError(e));
      }
    };
    poll();
    return () => { stop = true; };
  }, [token, returning, onReleased]);
  const start = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/claim/${token}/id-check/start`);
      window.location.assign(data.url);
    } catch (e) {
      setError(formatError(e));
      setBusy(false);
    }
  };
  const waiting = ["processing", "pending"].includes(s.status);
  const canStart = s.status === "not_started" || s.can_retry;
  return (
    <div data-testid="claim-id-check" className="space-y-4">
      <p className="text-sm text-emerald-700 dark:text-emerald-400 flex items-center gap-2"><ShieldCheck size={16} /> Details and code confirmed.</p>
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 p-4 text-sm space-y-2">
        <p className="font-semibold flex items-center gap-2"><ScanFace size={16} className="text-emerald-600" /> This letter needs an ID check</p>
        <p className="text-slate-600 dark:text-slate-300">Take a photo of your passport, driving licence or ID card and a quick selfie. We only keep the result (verified or not), never your ID images.</p>
      </div>
      {waiting && <p data-testid="claim-id-check-waiting" className="text-sm flex items-center gap-2"><Loader2 size={15} className="animate-spin" /> Checking your ID. This usually takes under a minute.</p>}
      {s.message && <p data-testid="claim-id-check-message" className={`text-sm flex gap-2 ${s.status === "verified" ? "text-emerald-700" : "text-red-600"}`}>{s.status !== "verified" && <AlertTriangle size={15} className="shrink-0 mt-0.5" />}{s.message}</p>}
      {error && <p data-testid="claim-id-check-error" className="text-sm text-red-600">{error}</p>}
      {canStart && !waiting && (
        <button data-testid="claim-id-check-start-btn" onClick={start} disabled={busy} className="w-full h-11 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-500 transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2">
          {busy && <Loader2 size={15} className="animate-spin" />}{s.status === "not_started" ? "Verify my ID" : "Try the ID check again"}</button>
      )}
    </div>
  );
};
